import { normalizeEmotion } from "../../utils/emotion";
import { useRef, useState, useMemo, useEffect } from "react";
import { loadEaf } from "../../utils/eaf";
import { CONTENT_H, CONTENT_W, MIAOBAN_EAF_URLS } from "./chunk-0.jsx";
export function pickMiaobanEafUrl(emotion) {
  const e = normalizeEmotion(emotion);
  switch (e) {
    case 'angry':
      return MIAOBAN_EAF_URLS.angry;
    case 'sad':
      return MIAOBAN_EAF_URLS.sad;
    case 'sleepy':
      return MIAOBAN_EAF_URLS.sleepy;
    case 'surprised':
      return MIAOBAN_EAF_URLS.shocked;
    case 'confused':
    case 'thinking':
      return MIAOBAN_EAF_URLS.confused;
    case 'winking':
      return MIAOBAN_EAF_URLS.winking;
    case 'neutral':
      return MIAOBAN_EAF_URLS.neutral;
    case 'cool':
      return MIAOBAN_EAF_URLS.winking;
    case 'happy':
    case 'laughing':
    case 'loving':
    case 'embarrassed':
    case 'relaxed':
    case 'silly':
    case 'confident':
    case 'delicious':
    default:
      return MIAOBAN_EAF_URLS.happy;
  }
}
export function pickMiaobanLayout(variant) {
  const v = String(variant || '').toLowerCase();
  // Default EmoteDisplay UI (esp-box-3 / xingzhi-cube): GFX_ALIGN_LEFT_MID + (10, 30)
  if (['', 'default', 'box3', 'esp-box-3', 'xingzhi', 'cube', '1_54', '1_83'].includes(v)) return {
    x: 10,
    y: 30
  };
  // EchoEar layout.json: GFX_ALIGN_LEFT_MID + (10, 10)
  if (['echoear', 'miaoban', '喵伴'].includes(v)) return {
    x: 10,
    y: 10
  };
  return {
    x: 10,
    y: 30
  };
}
export function MiaobanEyes({
  emotion,
  variant,
  poke,
  heartbeatRef,
  active = true,
  motionBoost = false
}) {
  const canvasRef = useRef(null);
  const offscreenRef = useRef(null);
  const animRef = useRef({
    raf: 0,
    start: 0,
    lastFrame: -1,
    lastPoke: 0
  });
  const [eaf, setEaf] = useState(null);
  const eafUrl = useMemo(() => pickMiaobanEafUrl(emotion), [emotion]);
  useEffect(() => {
    let cancelled = false;
    setEaf(null);
    loadEaf(eafUrl).then(data => {
      if (cancelled) return;
      setEaf(data);
    }).catch(() => {
      if (cancelled) return;
      setEaf(null);
    });
    return () => {
      cancelled = true;
    };
  }, [eafUrl]);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const drawFallback = () => {
      ctx.clearRect(0, 0, CONTENT_W, CONTENT_H);
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, CONTENT_W, CONTENT_H);
    };
    if (!eaf?.frames?.length) {
      drawFallback();
      return;
    }
    if (!offscreenRef.current) {
      offscreenRef.current = document.createElement('canvas');
    }
    const off = offscreenRef.current;
    off.width = eaf.width;
    off.height = eaf.height;
    const offCtx = off.getContext('2d');
    if (!offCtx) {
      drawFallback();
      return;
    }
    const fpsBase = 20;
    const fps = Math.round(fpsBase * (motionBoost ? 1.35 : 1));
    const frameCount = eaf.frames.length;
    const screenW = Math.min(CONTENT_W, CONTENT_H);
    const screenH = CONTENT_H;
    const screenOffsetX = Math.max(0, Math.floor((CONTENT_W - screenW) / 2));
    const layout = pickMiaobanLayout(variant);
    const objX = layout.x;

    // EmoteDisplay: GFX_ALIGN_LEFT_MID + (x,y) with auto-mirror.
    // Keep native EAF pixel size when it fits; scale down only if needed.
    const centerY = screenH / 2 + layout.y;
    const maxW = Math.max(1, Math.floor(screenW / 2 - objX));
    const maxHalfH = Math.max(0, Math.min(centerY, screenH - centerY));
    const maxH = Math.max(1, Math.floor(maxHalfH * 2));
    const scale = Math.min(1, Math.min(maxW / eaf.width, maxH / eaf.height));
    const drawW = Math.max(1, Math.round(eaf.width * scale));
    const drawH = Math.max(1, Math.round(eaf.height * scale));
    const topY = Math.round(centerY - drawH / 2);
    const leftX = screenOffsetX + objX;
    const rightX = screenOffsetX + screenW - objX - drawW;
    animRef.current.start = performance.now();
    animRef.current.lastFrame = -1;
    const render = t => {
      if (!canvasRef.current) return;
      if (!active) return;
      const pokeKey = Number(poke || 0) >>> 0;
      if (pokeKey !== animRef.current.lastPoke) {
        animRef.current.lastPoke = pokeKey;
        animRef.current.start = t;
        animRef.current.lastFrame = -1;
      }
      const ageMs = Math.max(0, t - (animRef.current.start || 0));
      const idx = frameCount ? Math.floor(ageMs / 1000 * fps) % frameCount : 0;
      const frameKey = idx + pokeKey * 1000;
      if (frameKey !== animRef.current.lastFrame) {
        animRef.current.lastFrame = frameKey;
        offCtx.putImageData(eaf.frames[idx], 0, 0);
        ctx.clearRect(0, 0, CONTENT_W, CONTENT_H);
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, CONTENT_W, CONTENT_H);
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(off, leftX, topY, drawW, drawH);
        ctx.save();
        ctx.translate(rightX + drawW, topY);
        ctx.scale(-1, 1);
        ctx.drawImage(off, 0, 0, drawW, drawH);
        ctx.restore();
        if (heartbeatRef) heartbeatRef.current = Date.now();
      }
      animRef.current.raf = requestAnimationFrame(render);
    };
    animRef.current.raf = requestAnimationFrame(render);
    return () => {
      if (animRef.current.raf) cancelAnimationFrame(animRef.current.raf);
      animRef.current.raf = 0;
    };
  }, [eaf, poke, active, motionBoost, heartbeatRef, variant]);
  return <canvas ref={canvasRef} width={CONTENT_W} height={CONTENT_H} className="w-full h-full" aria-label="miaoban-eaf" />;
}
